import { T, useI18n } from '@/i18n';
import { Icon } from '@/ui/icons';
import * as Popover from '@radix-ui/react-popover';
import { addDays, endOfMonth, format, startOfMonth, startOfWeek } from 'date-fns';
import { enGB, lt, nb, pl } from 'date-fns/locale';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';

export type PeriodMode = 'day' | 'week' | 'two' | 'three' | 'month';
export function periodRange(day: Date, mode: PeriodMode) {
  const from =
    mode === 'month'
      ? startOfMonth(day)
      : mode === 'day'
        ? day
        : addDays(startOfWeek(day, { weekStartsOn: 1 }), mode === 'three' ? -7 : 0);
  return {
    from,
    to:
      mode === 'month'
        ? endOfMonth(day)
        : addDays(from, mode === 'three' ? 20 : mode === 'two' ? 13 : mode === 'week' ? 6 : 0),
  };
}

/** A calendar selection represents the same period as the view it controls. */
export function DateJump({
  value,
  onChange,
  children,
  month = false,
  period,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  month?: boolean;
  period?: PeriodMode;
  /** the owning control's class for the trigger (PeriodControl); `datejump` stays for the icon rules */
  className?: string;
}) {
  const locale = useI18n((s) => s.locale);
  const calendarLocale = { nb, en: enGB, lt, pl }[locale];
  const mode = period ?? (month ? 'month' : 'day');
  const initial = () => new Date(`${value.length === 7 ? `${value}-01` : value}T12:00:00`);
  const [open, setOpen] = useState(false);
  // The room for the calendar, measured from its own button while it is open: below, or above when
  // there is more. Radix's own available-height variable was wrong under browser zoom (326 px said,
  // 231 px free at 200 %), and the calendar ran off the screen (#154 VF-07). It scrolls inside that
  // room, and its footer with «Vis perioden» stays at the bottom of what shows.
  const trigger = useRef<HTMLButtonElement>(null);
  const [room, setRoom] = useState<{ side: 'bottom' | 'top'; height: number } | null>(null);
  // Less room than the whole calendar needs (about 470 px with its footer): nothing in it stays pinned,
  // and «Vis perioden» sits in its top row, so focus can scroll every day and arrow into view (review
  // UI01-2: a pinned footer covered the focused day at 250x350)
  const short = !!room && room.height < 470;
  // Whatever has focus in the calendar is scrolled into its view: Radix moves focus with preventScroll
  // (opening, and Tab wrapping round to «Vis perioden»), and the short layout settles after the first
  // focus (review UI01-2)
  const content = useRef<HTMLDivElement>(null);
  const keepInView = () => {
    const a = document.activeElement;
    if (a instanceof HTMLElement && content.current?.contains(a))
      a.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };
  // «Vis perioden» is the next step from any day (review UI01-4): the day picker keeps its own Tab stop
  // on one day of the month, so after choosing a day further on (7 November, with 1 November the stop)
  // the browser's Tab left the calendar and closed it unapplied. And it keeps focus when the room moves
  // it between the top row and the foot (390x844 to 1440x900 dropped focus to the page).
  const apply = useRef<HTMLButtonElement>(null);
  const applyFocused = useRef(false);
  const onKeyDown = (e: { key: string; shiftKey: boolean; preventDefault: () => void }) => {
    if (e.key !== 'Tab' || e.shiftKey) return;
    if (!(document.activeElement instanceof HTMLElement && document.activeElement.closest('.rdp-day')))
      return;
    e.preventDefault();
    // after Radix's own Tab handler on the same element has run: focusing now, it saw the button as its
    // last stop and wrapped round to the first control
    queueMicrotask(() => apply.current?.focus());
  };
  useEffect(() => {
    if (!open) applyFocused.current = false;
  }, [open]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: when the layout moves the button, focus follows it
  useEffect(() => {
    if (open && applyFocused.current && document.activeElement !== apply.current) apply.current?.focus();
  }, [short]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: again once the room, and so the layout, is known
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(keepInView);
    return () => cancelAnimationFrame(id);
  }, [open, room]);
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const r = trigger.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom;
      const above = r.top;
      setRoom({
        side: below >= above ? 'bottom' : 'top',
        height: Math.max(96, Math.max(below, above) - 6 - 12),
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open]);
  const [draft, setDraft] = useState(initial);
  const [visible, setVisible] = useState(initial);
  const range = periodRange(draft, mode);
  const label = T(
    mode === 'month'
      ? 'Måned'
      : mode === 'three'
        ? '3 uker'
        : mode === 'two'
          ? 'To uker'
          : mode === 'week'
            ? 'Uke'
            : 'Dag',
  );
  const summary =
    mode === 'month'
      ? format(range.from, 'LLLL yyyy', { locale: calendarLocale })
      : `${format(range.from, 'd. MMM', { locale: calendarLocale })}${mode === 'day' ? '' : ` – ${format(range.to, 'd. MMM yyyy', { locale: calendarLocale })}`}`;
  const actions = (
    <div className={short ? 'datejump-footer top' : 'datejump-footer'}>
      <span aria-live="polite">{summary}</span>
      <button
        ref={apply}
        type="button"
        className="btn"
        onFocus={() => {
          applyFocused.current = true;
        }}
        onBlur={(e) => {
          // a move to another control; the layout taking the button away blurs with no target
          if (e.relatedTarget) applyFocused.current = false;
        }}
        onClick={() => {
          onChange(
            format(mode === 'three' ? addDays(range.from, 7) : range.from, month ? 'yyyy-MM' : 'yyyy-MM-dd'),
          );
          setOpen(false);
        }}
      >
        {T('Vis perioden')}
      </button>
    </div>
  );
  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setDraft(initial());
          setVisible(initial());
        }
      }}
    >
      <Popover.Trigger asChild>
        <button
          ref={trigger}
          type="button"
          className={className ? `datejump ${className}` : 'datejump'}
          title={T('Velg dato')}
        >
          <Icon name="calendar" />
          {children}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          ref={content}
          onFocus={keepInView}
          onKeyDown={onKeyDown}
          className="pop datejump-pop"
          side={room?.side ?? 'bottom'}
          sideOffset={6}
          collisionPadding={12}
          style={room ? { maxHeight: room.height } : undefined}
          aria-label={label}
        >
          <div className="datejump-heading">{label}</div>
          {short && actions}
          <DayPicker
            locale={calendarLocale}
            weekStartsOn={1}
            showOutsideDays
            fixedWeeks
            month={visible}
            onMonthChange={(d) => {
              setVisible(d);
              if (mode === 'month') setDraft(d);
            }}
            captionLayout="dropdown"
            startMonth={new Date(2000, 0)}
            endMonth={new Date(2100, 11)}
            modifiers={{
              selected: range,
              range_start: range.from,
              range_end: range.to,
              range_middle: (d) => d > range.from && d < range.to,
            }}
            onDayClick={(d) => setDraft(d)}
            onDayKeyDown={(d, _, e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setDraft(d);
              }
            }}
          />
          {!short && actions}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function validPeriod(value: string, month: boolean): boolean {
  if (!(month ? /^\d{4}-(0[1-9]|1[0-2])$/ : /^\d{4}-(0[1-9]|1[0-2])-\d{2}$/).test(value)) return false;
  const full = month ? `${value}-01` : value;
  const parsed = new Date(`${full}T12:00:00Z`);
  return (
    Number(full.slice(0, 4)) > 0 &&
    Number.isFinite(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === full
  );
}
